/**
 * Response API endpoints for answer submission.
 */

import { apiClient } from './client';
import type {
  SubmitAnswerRequest,
  SubmitAnswerResponse,
  SubmitPageRequest,
  SubmitPageResponse,
  ResponseData,
  AnswerValue,
} from '@/types';

/**
 * Submit a single answer.
 */
export async function submitAnswer(
  sessionId: string,
  answer: SubmitAnswerRequest
): Promise<SubmitAnswerResponse> {
  const response = await apiClient.post<SubmitAnswerResponse>(
    `/api/v1/player/sessions/${sessionId}/answers`,
    answer
  );
  return response.data;
}

/**
 * Submit all answers for a page.
 */
export async function submitPageAnswers(
  sessionId: string,
  pageData: SubmitPageRequest
): Promise<SubmitPageResponse> {
  const response = await apiClient.post<SubmitPageResponse>(
    `/api/v1/player/sessions/${sessionId}/pages`,
    pageData
  );
  return response.data;
}

/**
 * Get current response data for session recovery.
 */
export async function getResponseData(
  sessionId: string
): Promise<ResponseData> {
  const response = await apiClient.get<ResponseData>(
    `/api/v1/player/sessions/${sessionId}/response`
  );
  return response.data;
}

/**
 * Get a specific answer value.
 */
export async function getAnswer(
  sessionId: string,
  questionCode: string
): Promise<{ questionCode: string; value: AnswerValue }> {
  const response = await apiClient.get<{
    questionCode: string;
    value: AnswerValue;
  }>(`/api/v1/player/sessions/${sessionId}/answers/${questionCode}`);
  return response.data;
}

/**
 * Submit all answers at once (batch).
 */
export async function submitAllAnswers(
  sessionId: string,
  answers: SubmitAnswerRequest[],
  totalTimeSpent?: number,
  markComplete = false
): Promise<ResponseData> {
  const response = await apiClient.post<ResponseData>(
    `/api/v1/player/sessions/${sessionId}/submit`,
    {
      answers,
      totalTimeSpent,
      markComplete,
    }
  );
  return response.data;
}
